import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Mic, Search, LayoutGrid, FileText, Trophy, Factory, Globe2, Sparkles, Zap, Wand2,
  ChevronLeft, ChevronRight, Eye, Loader2, Heart, BadgeCheck, Megaphone,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { searchByImage, searchByVoice } from "../lib/media";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useSiteConfig } from "../context/ConfigContext";
import GeneralQuoteDialog from "../components/GeneralQuoteDialog";
import AuctionSection from "../components/AuctionSection";

const QUICK_CARDS = [
  { key: "quick_categories", icon: LayoutGrid, iconCls: "bg-orange-100 text-orange-500", to: "/categories" },
  { key: "quick_rfq", icon: FileText, iconCls: "bg-red-100 text-red-500" },
  { key: "quick_ranking", icon: Trophy, iconCls: "bg-amber-100 text-amber-500", to: "/products?sort=rating" },
  { key: "quick_custom", icon: Wand2, iconCls: "bg-violet-100 text-violet-500", action: "custom" },
  { key: "quick_manu", icon: Factory, iconCls: "bg-teal-100 text-teal-700", to: "/suppliers" },
  { key: "quick_worldwide", icon: Globe2, iconCls: "bg-teal-100 text-teal-600", action: "worldwide" },
];

const bannerMedia = (b) => {
  const img = b.image || b.image_url || (b.image_id ? `/api/media/${b.image_id}/large` : null);
  const vid = b.video || b.video_url || (b.video_id ? `/api/media/${b.video_id}/large` : null);
  return { img: img ? mediaUrl(img) : null, vid: vid ? mediaUrl(vid) : null };
};

function useWeekCountdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = (() => {
    const d = new Date();
    const e = new Date(d);
    e.setDate(d.getDate() + ((7 - d.getDay()) % 7));
    e.setHours(23, 59, 59, 999);
    return e.getTime();
  })();
  const diff = Math.max(0, Math.floor((end - now) / 1000));
  return {
    d: Math.floor(diff / 86400),
    h: Math.floor((diff % 86400) / 3600),
    m: Math.floor((diff % 3600) / 60),
  };
}

export default function Home() {
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const { features, auction } = useSiteConfig();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [live, setLive] = useState([]);
  const [countries, setCountries] = useState([]);
  const [banners, setBanners] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [announcement, setAnnouncement] = useState(null);
  const [q, setQ] = useState("");
  const [pill, setPill] = useState("all");
  const [rfqOpen, setRfqOpen] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [wish, setWish] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("simashi_wish") || "[]");
    } catch {
      return [];
    }
  });
  const fileRef = useRef(null);
  const recRef = useRef(null);
  const dealsRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();
  const cd = useWeekCountdown();

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const batch = await api(`/products?limit=24&page=${next}`);
      if (!Array.isArray(batch) || batch.length === 0) {
        setHasMore(false);
        return;
      }
      pageRef.current = next;
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...batch.filter((p) => !seen.has(p.id))];
      });
      if (batch.length < 24) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // Endless feed: load the next page when the bottom of the feed approaches
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && loadMore(),
      { rootMargin: "600px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore]);

  useEffect(() => {
    api("/products?limit=24").then(setProducts).catch(() => {});
    api("/home")
      .then((h) => setLive((h.live || []).filter((s) => s.status === "live")))
      .catch(() => {});
    api("/suppliers")
      .then((list) => {
        const m = {};
        list.forEach((s) => {
          if (s.country) m[s.country] = (m[s.country] || 0) + 1;
        });
        setCountries(Object.entries(m).sort((a, b) => b[1] - a[1]));
      })
      .catch(() => {});
    api("/banners")
      .then((b) => setBanners(Array.isArray(b) ? b.filter((x) => x.active !== false) : []))
      .catch(() => {});
    api("/config")
      .then((c) => c.announcement && setAnnouncement(c.announcement))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const tm = setInterval(() => setBannerIdx((i) => (i + 1) % banners.length), 5000);
    return () => clearInterval(tm);
  }, [banners.length]);

  useEffect(() => {
    if (location.hash === "#worldwide") {
      setTimeout(() => document.getElementById("worldwide-section")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash]);

  const picked = products.slice(0, 8);
  const deals = products.filter((p) => p.deal_pct > 0);
  const feed = products.filter((p) =>
    pill === "deals" ? p.deal_pct > 0 : pill === "custom" ? p.customizable : pill === "ready" ? p.ready_to_ship : true
  );

  const PILLS = [
    { key: "all", label: t("pill_all"), dot: "❤" },
    { key: "deals", label: t("pill_deals"), dot: "🏷" },
    { key: "custom", label: t("pill_custom"), dot: "✦" },
    { key: "ready", label: t("pill_ready"), dot: "⚡" },
  ];

  const toggleWish = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const next = wish.includes(id) ? wish.filter((x) => x !== id) : [...wish, id];
    setWish(next);
    localStorage.setItem("simashi_wish", JSON.stringify(next));
  };

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/products?q=${encodeURIComponent(q.trim())}` : "/products");
  };

  const onCamera = () => {
    if (!user) {
      toast.info("Log in to search by photo");
      navigate("/login?next=/");
      return;
    }
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImgLoading(true);
    try {
      const r = await searchByImage(f);
      const items = r.items || [];
      if (!items.length) {
        toast.info("No matching products found", { description: (r.terms || []).slice(0, 3).join(", ") });
      }
      navigate("/products", { state: { imageResults: items, imageTerms: r.terms || [] } });
    } catch (err) {
      toast.error("Image search failed", { description: err.message });
    } finally {
      setImgLoading(false);
    }
  };

  const onMic = async () => {
    if (!user) {
      toast.info("Log in to use voice search");
      navigate("/login?next=/");
      return;
    }
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (ev) => chunks.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        try {
          const r = await searchByVoice(blob);
          if (r.text) {
            setQ(r.text);
            navigate(`/products?q=${encodeURIComponent(r.text)}`);
          } else {
            toast.info("Couldn't hear that — try again");
          }
        } catch (err) {
          toast.error("Voice search failed", { description: err.message });
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      toast.info("Listening… tap the mic again to stop and search");
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const scrollDeals = (dir) => dealsRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  return (
    <div data-testid="home-page" className="bg-white">
      {announcement && (
        <div data-testid="announcement-bar" className="bg-teal-900 text-teal-50 text-center text-xs sm:text-sm font-medium px-4 py-2">
          {announcement}
        </div>
      )}
      <div className="bg-white pb-6">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <form
            onSubmit={submitSearch}
            role="search"
            className="flex items-center gap-2 rounded-full border-2 bg-white p-1.5 shadow-md shadow-orange-900/10"
            style={{ borderColor: "var(--ap, #EA580C)" }}
          >
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="image-search-file-input" onChange={onFile} />
          <button
            type="button"
            data-testid="search-camera-button"
            onClick={onCamera}
            disabled={imgLoading}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-100 transition disabled:opacity-60"
            aria-label={t("image_chip")}
          >
            {imgLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          </button>
          <input
            data-testid="hero-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("home_search_ph")}
            className="flex-1 min-w-0 bg-transparent px-1 py-2.5 text-base text-slate-900 outline-none placeholder:text-slate-400"
          />
          <button
            type="button"
            data-testid="search-mic-button"
            onClick={onMic}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition ${
              recording ? "bg-red-100 text-red-600 animate-pulse" : "text-slate-500 hover:bg-slate-50"
            }`}
            aria-label="Voice search"
          >
            <Mic className="h-5 w-5" />
          </button>
          <button
            data-testid="hero-search-button"
            type="submit"
            className="flex h-11 w-11 sm:w-14 shrink-0 items-center justify-center rounded-2xl bg-teal-700 text-white hover:bg-teal-800 transition active:scale-95 shadow-md shadow-teal-700/25"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
        </form>

        <div className="mt-4 flex gap-3 overflow-x-auto pb-1 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {QUICK_CARDS.filter((c) => c.key !== "quick_rfq" || features.rfq !== false).map(({ key, icon: Icon, iconCls, to, action }) => {
            const inner = (
              <>
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconCls}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-slate-900 leading-tight">{t(key)}</p>
                  <p className="text-[11px] text-slate-500 truncate">{t(`${key}_sub`)}</p>
                </div>
              </>
            );
            const cls =
              "flex w-40 sm:w-44 shrink-0 snap-start items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3.5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-teal-500/40";
            if (to)
              return (
                <Link key={key} data-testid={`quick-card-${key.replace("quick_", "")}`} to={to} className={cls}>{inner}</Link>
              );
            const onQuick = () => {
              if (key === "quick_rfq") setRfqOpen(true);
              else if (key === "quick_custom") {
                setPill("custom");
                document.getElementById("just-for-you")?.scrollIntoView({ behavior: "smooth" });
              } else document.getElementById("worldwide-section")?.scrollIntoView({ behavior: "smooth" });
            };
            return (
              <button key={key} data-testid={key === "quick_rfq" ? "quick-card-rfq" : key === "quick_worldwide" ? "quick-card-worldwide" : "quick-card-custom"} type="button" onClick={onQuick} className={cls}>
                {inner}
              </button>
            );
          })}
        </div>
        </section>
      </div>

      {auction?.position !== "bottom" && <AuctionSection auction={auction} />}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        {banners.length > 0 && (
          <div data-testid="ad-banners" className="mt-4 relative">
            {banners.map((b, i) => {
              const { img, vid } = bannerMedia(b);
              const content = (
                <div className={`relative overflow-hidden rounded-3xl bg-teal-950 aspect-[16/9] sm:aspect-[21/6] transition-opacity duration-700 ${i === bannerIdx ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                  {vid ? (
                    <video src={vid} autoPlay muted loop playsInline className="h-full w-full object-cover" />
                  ) : (
                    img && <img src={img} alt={b.title || "banner"} className="h-full w-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
                  <div className="absolute left-5 sm:left-8 top-1/2 -translate-y-1/2 max-w-md">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/90">
                      <Megaphone className="h-3 w-3" /> {t("sponsored")}
                    </span>
                    {b.title && <p className="font-heading mt-2 text-xl sm:text-3xl font-extrabold text-white leading-tight">{b.title}</p>}
                  </div>
                </div>
              );
              return b.link ? (
                <a key={b.id || i} data-testid={`banner-${i}`} href={b.link} target={b.link.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
                  {content}
                </a>
              ) : (
                <div key={b.id || i} data-testid={`banner-${i}`}>{content}</div>
              );
            })}
            {banners.length > 1 && (
              <div className="absolute bottom-3 right-4 flex gap-1.5">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`banner-dot-${i}`}
                    onClick={() => setBannerIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === bannerIdx ? "w-5 bg-white" : "w-1.5 bg-white/50"}`}
                    aria-label={`Banner ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading flex items-center gap-2 text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
            <Sparkles className="h-5 w-5 text-teal-600" /> {t("picked")}
          </h2>
          <Link data-testid="picked-see-all" to="/products" className="text-sm font-semibold text-teal-700 hover:text-teal-800">
            {t("see_all")} &rsaquo;
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {picked.map((p) => (
            <Link
              key={p.id}
              data-testid={`picked-card-${p.id}`}
              to={`/product/${p.id}`}
              className="group w-36 sm:w-44 shrink-0 snap-start"
            >
              <div className="relative aspect-square overflow-hidden rounded-2xl bg-teal-50">
                {p.image && (
                  <img src={mediaUrl(p.thumb || p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                <span className="absolute bottom-2 left-2 rounded-full bg-white/95 px-2.5 py-0.5 text-xs font-extrabold text-slate-900 shadow">
                  {fmt(p.price_min ?? p.price_max)}
                </span>
              </div>
              <p className="mt-2 text-xs font-semibold text-slate-800 leading-snug line-clamp-2">{p.title}</p>
            </Link>
          ))}
        </div>
      </section>

      {features.flash_deals !== false && deals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div data-testid="flash-section" className="rounded-3xl bg-[#431407] p-5 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="font-heading flex items-center gap-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                <Zap className="h-7 w-7 fill-orange-400 text-orange-400" /> {t("flash")}
              </h2>
              <div data-testid="flash-countdown" className="flex items-center gap-2 text-white text-sm font-semibold">
                <span className="hidden sm:inline">{t("ends_in")}</span>
                {[`${cd.d}d`, `${String(cd.h).padStart(2, "0")}h`, `${String(cd.m).padStart(2, "0")}m`].map((v, i) => (
                  <span key={i} className="flex items-center gap-2">
                    <span className="rounded-lg bg-[#083D3B] px-2.5 py-1.5 font-mono text-sm font-bold">{v}</span>
                    {i < 2 && <span>:</span>}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative">
              <button
                data-testid="flash-prev"
                onClick={() => scrollDeals(-1)}
                className="absolute -left-3 top-1/2 z-10 hidden md:flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-teal-800 shadow-lg hover:bg-teal-50"
                aria-label="Previous deals"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div ref={dealsRef} className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {deals.map((p) => {
                  const deal = fmt(p.price_min * (1 - p.deal_pct / 100));
                  return (
                    <Link
                      key={p.id}
                      data-testid={`flash-card-${p.id}`}
                      to={`/product/${p.id}`}
                      className="w-44 sm:w-56 shrink-0 snap-start overflow-hidden rounded-2xl bg-white transition-transform duration-300 hover:-translate-y-1"
                    >
                      <div className="relative aspect-square overflow-hidden bg-teal-50">
                        <span className="absolute left-0 top-3 z-10 rounded-r-full bg-orange-500 px-2.5 py-1 text-xs font-extrabold text-white">
                          -{p.deal_pct}%
                        </span>
                        {p.image && (
                          <img src={mediaUrl(p.thumb || p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div className="p-3.5">
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-extrabold text-teal-700">{deal}</span>
                          <span className="text-xs text-slate-400 line-through">{fmt(p.price_min)}</span>
                        </div>
                        {p.sold > 0 ? (
                          <>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.min(100, p.sold)}%` }} />
                            </div>
                            <p className="mt-1 text-[11px] text-slate-500">{Math.min(100, p.sold)}% {t("claimed")}</p>
                          </>
                        ) : (
                          <p className="mt-2 text-[11px] font-semibold text-orange-600">{t("fresh_deal")}</p>
                        )}
                        <h3 className="font-heading mt-1.5 text-sm font-bold text-slate-900 truncate">{p.title}</h3>
                        <p className="text-xs text-slate-500 capitalize truncate">{p.category} · per {p.unit || "piece"}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <button
                data-testid="flash-next"
                onClick={() => scrollDeals(1)}
                className="absolute -right-3 top-1/2 z-10 hidden md:flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-teal-800 shadow-lg hover:bg-teal-50"
                aria-label="Next deals"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>
      )}

      {features.live !== false && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading flex items-center gap-2.5 text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
            <span className="h-3 w-3 rounded-full bg-red-600 animate-pulse" /> {t("live_now")}
          </h2>
          <Link data-testid="live-see-all" to="/live" className="text-sm font-semibold text-teal-700 hover:text-teal-800">
            {t("see_all")} &rsaquo;
          </Link>
        </div>
        {live.length === 0 ? (
          <div data-testid="home-live-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-500">
            {t("no_live")}
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-1 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {live.map((s) => (
              <Link
                key={s.id}
                data-testid={`live-card-${s.id}`}
                to={`/live/${s.id}`}
                className="group relative block w-72 sm:w-80 shrink-0 snap-start overflow-hidden rounded-2xl aspect-video bg-teal-950 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                {s.video_url ? (
                  <video src={mediaUrl(s.video_url)} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  s.thumbnail && (
                    <img src={mediaUrl(s.thumbnail)} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  )
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
                <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
                </span>
                <span className="absolute top-3 right-3 flex items-center gap-1 rounded-md bg-black/50 px-2 py-1 text-[11px] font-semibold text-white">
                  <Eye className="h-3 w-3" /> {s.viewers ?? 0}
                </span>
                <div className="absolute bottom-0 inset-x-0 p-3.5">
                  <p className="font-heading font-bold text-white truncate">{s.title}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white">
                      {(s.supplier_name || "S")[0]}
                    </span>
                    <span className="text-xs text-white/90 truncate">{s.supplier_name}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      )}

      <section id="just-for-you" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 scroll-mt-24">
        <h2 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mb-4">{t("just_for_you")}</h2>
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {PILLS.map((pl) => (
            <button
              key={pl.key}
              data-testid={`feed-pill-${pl.key}`}
              onClick={() => setPill(pl.key)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
                pill === pl.key ? "bg-teal-950 text-white shadow" : "bg-white border border-slate-200 text-slate-700 hover:border-teal-400"
              }`}
            >
              <span className={pill === pl.key ? "" : "opacity-70"}>{pl.dot}</span> {pl.label}
            </button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 pb-10">
          {feed.map((p) => (
            <Link
              key={p.id}
              data-testid={`feed-card-${p.id}`}
              to={`/product/${p.id}`}
              className="group block"
            >
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-teal-50">
                {p.image && (
                  <img src={mediaUrl(p.thumb || p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                <button
                  data-testid={`feed-wish-${p.id}`}
                  onClick={(e) => toggleWish(e, p.id)}
                  className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow transition active:scale-90"
                  aria-label="Favourite"
                >
                  <Heart className={`h-4 w-4 ${wish.includes(p.id) ? "fill-red-500 text-red-500" : "text-slate-500"}`} />
                </button>
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-2">
                <p data-testid={`feed-price-${p.id}`} className="text-base font-extrabold text-slate-900">{fmt(p.price_min ?? p.price_max)}</p>
                {p.moq > 1 && <p className="shrink-0 text-[11px] text-slate-400">MOQ {p.moq}</p>}
              </div>
              <p className="mt-0.5 text-xs text-slate-500 leading-snug line-clamp-2">{p.title}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {p.verified && (
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-teal-700">
                    <BadgeCheck className="h-3.5 w-3.5" /> {t("verified")}
                    {p.supplier_years > 0 && <span className="text-slate-400 font-normal">{p.supplier_years}yrs · {(p.country || "").slice(0, 2).toUpperCase()}</span>}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
        {/* Endless feed sentinel — loads the next page of products automatically */}
        <div ref={sentinelRef} data-testid="feed-sentinel" className="flex justify-center pt-6 pb-10">
          {loadingMore && <Loader2 className="h-6 w-6 animate-spin text-teal-600" />}
          {!hasMore && !loadingMore && <p data-testid="feed-end" className="text-xs text-slate-400">{t("feed_end")}</p>}
        </div>
      </section>

      {/* Endless feed sentinel is inside the feed section above */}
      {auction?.position === "bottom" && <AuctionSection auction={auction} />}

      <GeneralQuoteDialog open={rfqOpen} onOpenChange={setRfqOpen} />
    </div>
  );
}
